import * as React from "react";

import { toast } from "@needl-ai/common/components/ui/toaster";
import {
  type CallbacksType,
  type DocumentType,
  FILE_UPLOAD_SOURCES,
  type UploadSourceType,
  useInitUploadComplete,
} from "@needl-ai/common/upload/hooks/useInitUploadComplete";

import { useFileHandler } from "./useFileHandler";
import { useFileUploadConfig } from "./useFileUploadConfig";

import {
  type AttachmentStatus,
  useFileUploadContext,
} from "@/contexts/FileUploadContext";
import { shouldDisableFileUpload } from "@/utils/attachmentUtils";

export const useAskNeedlUpload = (
  source: UploadSourceType = FILE_UPLOAD_SOURCES.ASK_NEEDL
) => {
  const { state, dispatch } = useFileUploadContext();
  const { data: uploadConfig, isLoading: isUploadConfigLoading } =
    useFileUploadConfig(source);
  const { initUploadComplete, removeFile } = useInitUploadComplete();

  const uploadFiles = React.useCallback(
    (files: File[]) => {
      if (isUploadConfigLoading || !uploadConfig) {
        toast({
          title: "Upload not ready",
          description: "Please wait for upload configuration to load.",
          variant: "destructive",
        });
        return;
      }

      // Check if uploads should be disabled (waiting for feed_id)
      if (
        shouldDisableFileUpload(state.attachments, state.attachment_feed_id)
      ) {
        toast({
          title: "Upload not available",
          description:
            "Please wait for current upload to complete before adding more files.",
          variant: "destructive",
        });
        return;
      }

      // Check file count limit
      const currentCount = state.attachments.length;
      const totalCount = currentCount + files.length;

      if (totalCount > uploadConfig.file_count_limit) {
        toast({
          title: "Too many files",
          description: `You can only upload ${uploadConfig.file_count_limit} files at a time.`,
          variant: "destructive",
        });
        return;
      }

      // Generate file IDs that will be used consistently
      const fileIds = files.map(() => crypto.randomUUID());

      // Add files to context with the generated IDs
      const fileItems = files.map((file, index) => ({
        id: fileIds[index],
        name: file.name,
        mime_type: file.type,
        file: file,
        progress: 0,
        status: "UPLOADING" as AttachmentStatus,
      }));

      // Dispatch the actual file items instead of just the File objects
      dispatch({
        type: "ADD_ATTACHMENTS_WITH_IDS",
        payload: fileItems,
      });

      // Convert to DocumentType for the upload hook using the same IDs
      const documents: DocumentType[] = files.map((file, index) => ({
        id: fileIds[index],
        name: file.name,
        mime_type: file.type,
        file,
      }));

      const callbacks: CallbacksType = {
        onInitSuccess: (response) => {
          console.log("Upload init success:", response);

          // Set bucket/key info for each file from the upload configs - matching main app behavior
          const uploadConfigs = response.upload_configs || [];
          uploadConfigs.forEach((config) => {
            dispatch({
              type: "UPDATE_UPLOAD_STATUS",
              payload: {
                fileId: config.id,
                status: "UPLOADING",
                info: {
                  source,
                  bucket: config.metadata_fields.bucket,
                  key: config.metadata_fields.key,
                  metadata: {
                    fileName: config.name,
                    mimeType: config.mime_type,
                    guid: config.id,
                  },
                },
              },
            });
          });
        },

        onInitError: (error) => {
          console.error("Upload init error:", error);

          const fileIds = documents.map((doc) => doc.id);
          dispatch({
            type: "UPDATE_STATUS_OF_ATTACHMENTS",
            payload: {
              fileIds,
              status: "UPLOADING_ERROR",
            },
          });

          toast({
            title: "Upload failed",
            description: "Failed to initialize upload. Please try again.",
            variant: "destructive",
          });
        },

        onUploadSuccess: (_, document) => {
          console.log("Upload success:", document.name);

          // Update status to UPLOADED - bucket/key info already set in onInitSuccess
          dispatch({
            type: "UPDATE_STATUS_OF_ATTACHMENT",
            payload: {
              fileId: document.id,
              status: "UPLOADED",
            },
          });
        },

        onUploadError: (error, document) => {
          console.error("Upload error:", error);

          dispatch({
            type: "UPDATE_STATUS_OF_ATTACHMENTS",
            payload: {
              fileIds: [document.id],
              status: "UPLOADING_ERROR",
            },
          });

          toast({
            title: "Upload failed",
            description: `Failed to upload ${document.name}. Please try again.`,
            variant: "destructive",
          });
        },

        onUploadProgress: (percentProgress, document) => {
          dispatch({
            type: "UPDATE_PROGRESS",
            payload: {
              fileId: document.id,
              progress: percentProgress,
            },
          });
        },

        onUploadCompleteSuccess: (response) => {
          const fileIds = documents.map((doc) => doc.id);

          // First set to UPLOADED status
          dispatch({
            type: "UPDATE_STATUS_OF_ATTACHMENTS",
            payload: {
              fileIds,
              status: "UPLOADED",
            },
          });

          // Then immediately set to PROCESSING
          setTimeout(() => {
            dispatch({
              type: "UPDATE_STATUS_OF_ATTACHMENTS",
              payload: {
                fileIds,
                status: "PROCESSING",
              },
            });
          }, 100);

          // Set the attachment_feed_id to start polling
          if (response.feed_id) {
            dispatch({
              type: "SET_ATTACHMENT_FEED_ID",
              payload: response.feed_id,
            });
          }
        },

        onUploadCompleteError: (error) => {
          console.error("Upload complete error:", error);

          const fileIds = documents.map((doc) => doc.id);
          dispatch({
            type: "UPDATE_STATUS_OF_ATTACHMENTS",
            payload: {
              fileIds,
              status: "UPLOADING_ERROR",
            },
          });

          toast({
            title: "Upload failed",
            description: "Failed to complete upload. Please try again.",
            variant: "destructive",
          });
        },
      };

      // Start the upload process
      initUploadComplete(
        {
          documents,
          source,
          ...(state.attachment_feed_id
            ? { feed_id: state.attachment_feed_id }
            : {}),
        },
        callbacks
      );
    },
    [
      state.attachments,
      state.attachment_feed_id,
      uploadConfig,
      isUploadConfigLoading,
      initUploadComplete,
      dispatch,
      source,
    ]
  );

  const removeAttachment = React.useCallback(
    async (attachmentId: string) => {
      const fileToRemove = state.attachments.find(
        (file) => file.id === attachmentId
      );

      if (!fileToRemove) {
        console.warn("File not found for removal:", attachmentId);
        return;
      }

      // Remove from local state only after successful API call (or if no API call needed)
      dispatch({
        type: "REMOVE_ATTACHMENTS",
        payload: [attachmentId],
      });

      // Only call API for files that are completed or have processing errors, and have bucket/key info
      if (
        ["COMPLETED", "PROCESSING_ERROR"].includes(fileToRemove.status) &&
        fileToRemove.info?.bucket &&
        fileToRemove.info?.key &&
        state.attachment_feed_id
      ) {
        try {
          await removeFile(
            {
              source,
              files: [
                {
                  id: fileToRemove.id,
                  name: fileToRemove.name,
                  mime_type: fileToRemove.mime_type,
                  bucket: fileToRemove.info.bucket,
                  key: fileToRemove.info.key,
                },
              ],
              feed_id: state.attachment_feed_id,
            },
            {
              onRemoveSuccess: (response) => {
                console.log("File removed successfully:", response);
              },
              onRemoveError: (error) => {
                console.error("Error removing file:", error);
                toast({
                  title: "Error removing file",
                  description:
                    "Failed to remove file from server. Please try again.",
                  variant: "destructive",
                });
                return; // Don't remove from local state if API call fails
              },
            }
          );
        } catch (error) {
          console.error("Error removing file:", error);
          toast({
            title: "Error removing file",
            description: "Failed to remove file from server. Please try again.",
            variant: "destructive",
          });
          return; // Don't remove from local state if API call fails
        }
      }
    },
    [dispatch, state.attachments, state.attachment_feed_id, removeFile, source]
  );

  const clearAttachments = React.useCallback(() => {
    dispatch({
      type: "CLEAR_ATTACHMENTS",
    });
  }, [dispatch]);

  const clearAttachmentsOnly = React.useCallback(() => {
    dispatch({
      type: "CLEAR_ATTACHMENTS_ONLY",
    });
  }, [dispatch]);

  // File handler for drag/drop and file selection
  const {
    fileInputRef,
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInputClick,
    handleFileSelect,
  } = useFileHandler(
    uploadFiles,
    uploadConfig || {
      file_types: [],
      mime_types: [],
      file_size_limit: 0,
      file_name_suffixes: [],
      allow_folder_upload: false,
      file_count_limit: 0,
    },
    {
      onSizeLimitExceed: (file, message) => {
        toast({
          title: "File too large",
          description: message,
          variant: "destructive",
        });
      },
      onFileNotSupported: (file, message) => {
        toast({
          title: "Unsupported file type",
          description: message,
          variant: "destructive",
        });
      },
    }
  );

  return {
    attachments: state.attachments,
    feedId: state.attachment_feed_id,
    isUploading: state.attachments.some((file) => file.status === "UPLOADING"),
    uploadFiles,
    removeAttachment,
    clearAttachments,
    clearAttachmentsOnly,
    fileInputRef,
    handleFileInputClick: () => handleFileInputClick("file"),
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    isDragging,
  };
};
